import React from 'react';

import { ChapterWrapper } from '~/ChapterWrapper';
import Footer from '~/Footer';
import { Path } from '~/paths';

// const nv_3models_intro = require('../assets/images/nv_3models_intro.png');


export const ChisquareDistributionMeta = {
    title: "Chí-kvadrát rozdělení *",
    shortTitle: "Chí-kvadrát rozdělení *",
    path: Path.chisquare_distribution,
    element: (sectionNumber:string) => <ChisquareDistribution sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec: string[]  = [
    "chisquare-formula",
    "chisquare-normal",
    "chisquare-sum",
];


export function ChisquareDistribution({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={ChisquareDistributionMeta.title} TOCSpec={TOCSpec}>

            <i>(* volitelná kapitola)</i>

            <h2 id="chisquare-formula">Chí-kvadrát rozdělení obecně</h2>
            <p>
            Hustota {"$\\chi^2$"} rozdělení s {"$k$"} stupni volnosti je dána vzorcem:
            {`$$
            \\begin{align*}
                f(x) &= \\frac{1}{2^{\\frac{k}{2}}\\Gamma(\\frac{k}{2})}x^{\\frac{k}{2}-1}e^{-\\frac{x}{2}} \\\\
            \\end{align*}
            $$`}
            </p>
            <p>
            Pro malá {"$k$"} máme:
            {`$$
            \\begin{align*}
                k &= 1 & f(x) &= \\frac{1}{2^{\\frac{1}{2}}\\sqrt{\\pi}}x^{\\frac{1}{2}-1}e^{-\\frac{x}{2}} &&= {\\color{BrickRed}{\\frac{1}{\\sqrt{2\\pi}}x^{-\\frac{1}{2}}e^{-\\frac{x}{2}}}} \\\\
                k &= 2 & f(x) &= \\frac{1}{2^{\\frac{2}{2}} \\cdot 1}x^{\\frac{2}{2}-1}e^{-\\frac{x}{2}} &&= \\frac{1}{2}e^{-\\frac{x}{2}} \\\\
                k &= 3 & f(x) &= \\frac{1}{2^{\\frac{3}{2}} \\cdot \\frac{1}{2}\\sqrt{\\pi}}x^{\\frac{3}{2}-1}e^{-\\frac{x}{2}} &&= \\frac{1}{\\sqrt{2\\pi}}x^{\\frac{1}{2}}e^{-\\frac{x}{2}} \\\\
                k &= 4 & f(x) &= \\frac{1}{2^{\\frac{4}{2}} \\cdot 1}x^{\\frac{4}{2}-1}e^{-\\frac{x}{2}} &&= \\frac{1}{4}xe^{-\\frac{x}{2}} \\\\
                & \\cdots && \\cdots && \\cdots
            \\end{align*}
            $$`}
            </p>

            <h2 id="chisquare-normal">Rozdělení {"$X^2$"} pro {"$X \\sim N(0,1)$"}</h2>
            <p>
            Když má náhodná veličina {"$X$"} standardní normální rozdělení, pak {"$X^2$"} má chí-kvadrát rozdělení s {"$k=1$"} jedním stupněm volnosti.
            </p>
            <p>
            Distribuční funkce:
            {`$$
            \\begin{align*}
                F_Y(y) &= P(Y \\leq y) = P(X^2 \\leq y) = P(-\\sqrt{y} \\leq X \\leq \\sqrt{y}) = \\Phi(\\sqrt{y}) - \\Phi(-\\sqrt{y}) = \\\\
                &= \\Phi(\\sqrt{y}) - (1 - \\Phi(\\sqrt{y})) = 2\\Phi(\\sqrt{y}) - 1 \\\\
            \\end{align*}
            $$`}
            </p>
            <p>
            Hustota:
            {`$$
            \\begin{align*}
                f_Y(y) &= \\frac{d}{dy}F_Y(y) = \\frac{d}{dy}(2\\Phi(\\sqrt{y}) - 1) = 2\\cdot\\Phi'(\\sqrt{y})\\cdot\\frac{1}{2\\sqrt{y}} = \\\\
                &= 2\\cdot\\frac{1}{\\sqrt{2\\pi}}e^{-\\frac{y}{2}}\\cdot\\frac{1}{2\\sqrt{y}} = \\\\
                &= {\\color{BrickRed}{\\frac{1}{\\sqrt{2\\pi}}y^{-\\frac{1}{2}}e^{-\\frac{y}{2}}}} \\\\
            \\end{align*}
            $$`}
            </p>

            <h2 id="chisquare-sum">Součet nezávislých veličin {"$\\sim \\chi^2(n)$"}</h2>
            <p>
            Mějme <strong>nezávislé</strong> náhodné veličiny {"$A \\sim \\chi^2(k)$"} a {"$B \\sim \\chi^2(l)$"}.
            Pak
            {`$$
            \\begin{align*}
                A + B \\sim \\chi^2(k+l)
            \\end{align*}
            $$`}
            Sdružená hustota je kvůli nezávislosti <strong>součinem hustot</strong>, pro výpočet použijeme <strong>konvoluci</strong>:
            {`$$
            \\begin{align*}
                f_{A+B}(x) &= \\int_0^x f_A(t)f_B(x-t)\\,\\text{d}x = \\\\
                &= \\int_0^x \\frac{1}{2^{\\frac{k}{2}}\\Gamma(\\frac{k}{2})}t^{\\frac{k}{2}-1}e^{-\\frac{t}{2}} \\cdot \\frac{1}{2^{\\frac{l}{2}}\\Gamma(\\frac{l}{2})}(x-t)^{\\frac{l}{2}-1}e^{-\\frac{x-t}{2}}\\,\\text{d}t = \\\\
                &= \\frac{1}{2^{\\frac{k+l}{2}}\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})}\\int_0^x t^{\\frac{k}{2}-1}(x-t)^{\\frac{l}{2}-1}e^{-\\frac{x}{2}}\\,\\text{d}t = \\\\
                &= |\\text{sub: } t = xs, \\ \\text{d}t = x\\,\\text{d}s| = \\\\
                &= \\frac{1}{2^{\\frac{k+l}{2}}\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})}\\int_0^1 ({\\color{green}{x}}s)^{\\frac{k}{2}-1}({\\color{green}{x}}(1-s))^{\\frac{l}{2}-1}e^{-\\frac{x}{2}}{\\color{green}{x}}\\,\\text{d}s = \\\\
                &= \\frac{1}{2^{\\frac{k+l}{2}}\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})} {\\color{green}{x^{\\frac{k+l}{2}-1}}}e^{-\\frac{x}{2}} {\\color{RoyalBlue}{\\int_0^1s^{\\frac{k}{2}-1}(1-s)^{\\frac{l}{2}-1}\\,\\text{d}s}} = \\\\
                &= \\frac{1}{2^{\\frac{k+l}{2}}\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})} x^{\\frac{k+l}{2}-1}e^{-\\frac{x}{2}} \\cdot  {\\color{RoyalBlue}{\\text{B}\\Big(\\frac{k}{2}, \\frac{l}{2}\\Big)}} = \\\\
                &= \\frac{1}{2^{\\frac{k+l}{2}}\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})} x^{\\frac{k+l}{2}-1}e^{-\\frac{x}{2}} \\cdot {\\color{RoyalBlue}{\\frac{\\Gamma(\\frac{k}{2})\\Gamma(\\frac{l}{2})}{\\Gamma(\\frac{k+l}{2})}}} = \\\\
                &= \\frac{1}{2^{{\\color{magenta}{\\frac{k+l}{2}}}}\\Gamma({\\color{magenta}{\\frac{k+l}{2}}})} x^{{\\color{magenta}{\\frac{k+l}{2}}}-1}e^{-\\frac{x}{2}} \\\\
            \\end{align*}
            $$`}
            Toto je hustota {"$\\chi^2({\\color{magenta}{k+l}})$"} rozdělení - viz vzorec úplně nahoře.
            </p>

            {/* 2\\cdot\\frac{1}{\\sqrt{2\\pi}}e^{-\\frac{y}{2}}\\cdot\\frac{1}{2\\sqrt{y}} \\\\ */}

            <Footer/>
        </ChapterWrapper>
    );
}

